/** @format */

import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	computed,
	Inject,
	input,
	OnInit,
	output,
	PLATFORM_ID,
	Signal
} from '@angular/core';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { HomefieldColorSwatchModule, HomefieldTextLinkModule } from '@homefield';

import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { IsBotService, WINDOW } from '@shared/data-access/services';

@Component({
	selector: 'shared-totf-components-product-card-color-swatches',
	imports: [
		CommonModule,
		HomefieldColorSwatchModule,
		HomefieldTextLinkModule,
		HmfActionTextComponent
	],
	templateUrl: './product-card-color-swatches.component.html',
	styleUrl: './product-card-color-swatches.component.scss'
})
export class ProductCardColorSwatchesComponent implements OnInit {
	assetSeoUrl = input('');
	colors = input<string[]>([]);
	ecode = input('');
	isLazy = input(true);

	extraColorsClick = output<PointerEvent>();
	selectedSwatchColorChange = output<string>();

	IMAGE_HOST = 'https://dks.scene7.com/is/image/dkscdn';
	maxColorSwatchesToShow = 3;
	selectedSwatchColor: string;
	moreColorsButtonAriaLabel = '';
	remainingColors = 0;
	readonly validColors: Signal<string[]> = computed(
		() => this.colors()?.filter((color) => color != null && color.trim() !== '') || []
	);

	constructor(
		private readonly isBotService: IsBotService,
		private readonly analyticsService: ProductAnalyticsService,
		private readonly cdr: ChangeDetectorRef,
		@Inject(PLATFORM_ID) private readonly platformId,
		@Inject(WINDOW) protected window: Window
	) {}

	ngOnInit(): void {
		if (!this.validColors().length) {
			return;
		}

		this.selectedSwatchColor = this.validColors()[0];

		this.setNumberOfSwatchesToShow();
		this.setRemainingColorsAndMoreColorsAriaLabel();
	}

	trackBySwatch(index: number, item: string): string {
		return `${index}-${item}`;
	}

	setImageColor(color: string): void {
		if (color === this.selectedSwatchColor) {
			return;
		}

		this.selectedSwatchColor = color;
		this.selectedSwatchColorChange.emit(color);
	}

	getImageSource(color: string): string {
		return `${this.IMAGE_HOST}/${this.ecode()}_${color.replace(
			/[^\w]/gi,
			'_'
		)}_swatch/?$DSG_ProductSwatch$&resMode=bilin&qlt=80`;
	}

	extraColorsClicked(event: PointerEvent): void {
		this.extraColorsClick.emit(event);
	}

	/**
	 * @description Posts to rumrunner when a missing product image is detected
	 */
	handleImageError(event: { color: string; requestedImg: string }): void {
		if (
			isPlatformBrowser(this.platformId) &&
			!this.isBotService.isBot(window.navigator.userAgent)
		) {
			this.analyticsService.emitMissingImageEvent({
				pageUrl: window.location.href,
				requestedImg: event.requestedImg,
				ecode: this.ecode(),
				color: event.color
			});
		}
	}

	private setNumberOfSwatchesToShow(): void {
		if (!isPlatformBrowser(this.platformId) || !this.validColors()?.length) {
			return;
		}

		const widthQuery = matchMedia('(max-width: 1531px)');

		// Initial check of the media query
		this.maxColorSwatchesToShow = widthQuery.matches ? 3 : 6;

		// Check when window width passes the media query
		widthQuery.onchange = (event) => {
			this.maxColorSwatchesToShow = event.matches ? 3 : 6;
			this.setRemainingColorsAndMoreColorsAriaLabel();
			this.cdr.detectChanges();
		};
	}

	private setRemainingColorsAndMoreColorsAriaLabel(): void {
		this.remainingColors = this.validColors().length - this.maxColorSwatchesToShow;
		this.moreColorsButtonAriaLabel = `Show ${this.remainingColors} more color option${this.remainingColors > 1 ? 's' : ''}.`;
	}
}
